import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { Endpoints } from '@lifeomic/react-client/generated/medical-results-service';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import urls from '../util/urls';
import { ServiceAuth } from '../hooks/useService/useService';
import { pick } from 'lodash';

interface Options {
  axiosOptions?: AxiosRequestConfig;
  [key: string]: any;
}

// --- Published types from @lifeomic/react-client (medical-results-service) ---

export type HistoryResponse =
  Endpoints['GET /v1/medical-results/patient/:patientId/history']['Response'];

export type V3Response =
  Endpoints['GET /v1/medical-results/v3/patient/:patientId/results']['Response'];

export type ResultHistory = HistoryResponse['items'][0];

export type ResultHistoryWithRange = Extract<
  ResultHistory,
  { type: 'withRange' }
>;

export type ResultHistoryWithoutRange = Exclude<
  ResultHistory,
  { type: 'withRange' }
>;

export type Ranges = ResultHistoryWithRange['ranges'];

export type Result = V3Response['items'][0];

export type IndividualResult = Result['results'][0];

export type ResultWithRange = Extract<IndividualResult, { type: 'withRange' }>;

export const isResultWithRange = (
  result: IndividualResult,
): result is ResultWithRange => result.type === 'withRange';

export const isResultHistoryWithRange = (
  result: ResultHistory,
): result is ResultHistoryWithRange => result.type === 'withRange';

export type MedicalResultSource =
  | 'mappedLabs'
  | 'unmappedLabs'
  | 'memberUpload';

interface BaseParams {
  patientId: string;
  project: string;
  nextPageToken?: string | null;
  pageSize?: number | null;
  cohort?: string;
  resultCount?: string;
  code?: string;
  system?: string;
  includeClinicOnly?: boolean;
  source?: MedicalResultSource;
}

export default class MedicalResultsService {
  medicalResultsAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(_options?: Partial<Options>) {
    this.medicalResultsAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getResults =
    <TResponse = V3Response>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.results(
          params.patientId,
          pick(params, ['project', 'nextPageToken', 'pageSize', 'cohort']),
        ),
      );
    };

  getResultsV3 =
    <TResponse = V3Response>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.resultsV3(
          params.patientId,
          pick(params, [
            'project',
            'nextPageToken',
            'pageSize',
            'cohort',
            'resultCount',
            'includeClinicOnly',
            'source',
          ]),
        ),
      );
    };

  getHistory =
    <TResponse = HistoryResponse>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.history(
          params.patientId,
          pick(params, [
            'project',
            'cohort',
            'code',
            'system',
            'includeClinicOnly',
            'source',
          ]),
        ),
      );
    };
}
